import React, { useState, useRef, useEffect } from 'react';
import { useProject } from '../context/ProjectContext';

interface NavbarProps {
  onNavigate: (page: 'dashboard' | 'tickets' | 'agents' | 'worklog' | 'settings') => void;
  currentPage: 'dashboard' | 'tickets' | 'agents' | 'jobs' | 'worklog' | 'settings';
  user?: any;
  onLogout?: () => void;
  onToggleCode?: () => void;
  isCodePanelOpen?: boolean;
}

const Navbar: React.FC<NavbarProps> = ({ onNavigate, currentPage, user, onLogout }) => {
  const { projects, currentProject, selectProject, hasJira } = useProject();
  const [showUserMenu, setShowUserMenu] = useState(false);
  const [showProjectMenu, setShowProjectMenu] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);
  const projectMenuRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        setShowUserMenu(false);
      }
      if (projectMenuRef.current && !projectMenuRef.current.contains(event.target as Node)) {
        setShowProjectMenu(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const getInitials = (name: string) => {
    if (!name) return '??';
    const parts = name.split(' ');
    if (parts.length >= 2) {
      return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
    }
    return name.substring(0, 2).toUpperCase();
  };

  const navItems: Array<{
    id: 'dashboard' | 'tickets' | 'agents' | 'worklog';
    label: string;
    active: boolean;
    show?: boolean;
  }> = [
    { id: 'dashboard', label: 'Dashboard', active: currentPage === 'dashboard' },
    // Tickets UI lives inside Worklog — keep id for legacy redirects, hide from menu
    { id: 'tickets', label: 'Tickets', active: false, show: false },
    { id: 'agents', label: 'Agents', active: currentPage === 'agents' || currentPage === 'jobs' },
    { id: 'worklog', label: 'Worklog', active: currentPage === 'worklog' || currentPage === 'tickets', show: hasJira },
  ];

  return (
    <nav className="relative z-50 complic-nav">
      <div className="px-5 md:px-8 py-3">
        <div className="flex items-center justify-between gap-4">
          {/* Brand + project */}
          <div className="flex-1 flex items-center gap-3 min-w-0">
            <div className="flex items-center gap-2 shrink-0">
              <div className="w-8 h-8 rounded-xl bg-gradient-to-br from-complic-500 to-sky-500 text-white flex items-center justify-center text-sm font-extrabold shadow-soft">
                C
              </div>
              <span className="hidden sm:inline text-base font-extrabold tracking-tight text-slate-900 dark:text-white">
                Complic
              </span>
            </div>

            <div className="relative inline-block min-w-0" ref={projectMenuRef}>
              <button
                onClick={() => setShowProjectMenu(!showProjectMenu)}
                className="flex items-center gap-2 max-w-[14rem] px-3 py-1.5 rounded-full bg-white/80 dark:bg-slate-800/80 border border-slate-200/80 dark:border-slate-600/60 text-slate-700 dark:text-slate-200 hover:border-complic-400/50 transition-all font-sans font-semibold text-sm shadow-soft"
              >
                <span className="truncate">{currentProject?.name || 'Select Project'}</span>
                <svg className={`w-3.5 h-3.5 shrink-0 transition-transform ${showProjectMenu ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
              </button>

              {showProjectMenu && projects.length > 0 && (
                <div className="absolute top-11 left-0 w-64 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-2xl overflow-hidden z-50 shadow-panel">
                  <div className="p-2">
                    {projects.map((project) => (
                      <button
                        key={project.id}
                        onClick={() => {
                          selectProject(project.id);
                          setShowProjectMenu(false);
                        }}
                        className={`w-full text-left px-3 py-2 rounded-xl transition-all ${
                          currentProject?.id === project.id
                            ? 'bg-complic-50 dark:bg-complic-900/40 text-complic-800 dark:text-complic-100 font-bold'
                            : 'text-slate-700 dark:text-slate-200 hover:bg-slate-50 dark:hover:bg-slate-800'
                        }`}
                      >
                        <div className="flex items-center justify-between gap-2">
                          <span className="font-sans text-sm truncate">{project.name}</span>
                          {project.is_default && (
                            <span className="text-[10px] bg-complic-500 text-white px-1.5 py-0.5 rounded-md shrink-0">Default</span>
                          )}
                        </div>
                        {project.user_role && (
                          <span className="text-xs text-slate-500 dark:text-slate-400 font-sans capitalize">{project.user_role}</span>
                        )}
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Center nav */}
          <div className="flex items-center gap-0.5 p-1 rounded-full bg-slate-100/80 dark:bg-slate-800/60 border border-slate-200/60 dark:border-slate-700/50">
            {navItems.filter((item) => item.show !== false).map((item) => (
              <button
                key={item.id}
                onClick={() => onNavigate(item.id)}
                className={`complic-nav-pill ${item.active ? 'is-active' : ''}`}
              >
                {item.label}
              </button>
            ))}
          </div>

          {/* Right */}
          <div className="flex-1 flex items-center justify-end gap-1.5">
            <button
              onClick={() => onNavigate('settings')}
              className={`complic-nav-pill ${currentPage === 'settings' ? 'is-active' : ''}`}
            >
              Settings
            </button>

            {user && (
              <div className="relative flex items-center" ref={menuRef}>
                <button
                  onClick={() => setShowUserMenu(!showUserMenu)}
                  className="w-9 h-9 rounded-full bg-gradient-to-br from-complic-500 to-sky-500 text-white text-xs font-bold flex items-center justify-center shadow-soft hover:scale-105 transition-transform"
                  title={user.name}
                >
                  {getInitials(user.name)}
                </button>

                {showUserMenu && (
                  <div className="absolute top-11 right-0 w-64 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-2xl overflow-hidden z-50 shadow-panel">
                    <div className="p-3 border-b border-slate-100 dark:border-slate-700">
                      <p className="text-sm font-sans font-bold text-slate-900 dark:text-white">{user.name}</p>
                      <p className="text-xs text-slate-500 dark:text-slate-400 truncate font-sans mt-0.5">{user.email}</p>
                    </div>

                    <button
                      onClick={() => {
                        setShowUserMenu(false);
                        onNavigate('settings');
                      }}
                      className="w-full text-left px-3 py-2.5 text-sm text-slate-700 dark:text-slate-200 hover:bg-slate-50 dark:hover:bg-slate-800 font-sans transition-all"
                    >
                      Settings
                    </button>

                    {onLogout && (
                      <button
                        onClick={() => {
                          setShowUserMenu(false);
                          onLogout();
                        }}
                        className="w-full text-left px-3 py-2.5 text-sm text-red-600 dark:text-red-400 hover:bg-slate-50 dark:hover:bg-slate-800 font-sans transition-all border-t border-slate-100 dark:border-slate-700"
                      >
                        Logout
                      </button>
                    )}
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    </nav>
  );
};

export default Navbar;
